import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { isNil, round } from 'lodash';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { SwellArrowIcon } from '@wavetrak/theme';

import type { Units } from 'types/units';
import type { Swell } from 'types/swell';
import formatWaveHeight from 'utils/formatWaveHeight';
import mapDegreesCardinal from 'utils/mapDegreesCardinal';

import styles from './SwellReading.module.scss';

interface Props {
  activeSwell: number;
  applySwellColors?: boolean;
  containerClassName?: string;
  setActiveSwell?: (swellIndex: number) => void;
  swell: Swell;
  units: Units;
}

const SwellReading: React.FC<Props> = ({
  activeSwell,
  applySwellColors = false,
  containerClassName = '',
  setActiveSwell,
  swell,
  units,
}) => {
  const theme = useTheme();

  const activeOrInactiveColor =
    swell.index === activeSwell ? theme.palette.text.primary : theme.palette.text.secondary;

  const swellDirectionSpread = useMemo(() => {
    if (!isNil(swell?.spread)) {
      return `±${round(swell.spread / 2)}°`;
    }
    if (!isNil(swell?.directionMin)) {
      return `±${round(swell.direction - swell.directionMin)}°`;
    }
    return '';
  }, [swell.direction, swell.directionMin, swell.spread]);

  const onClickSwellTile = useCallback(() => {
    if (setActiveSwell) setActiveSwell(swell.index);
  }, [setActiveSwell, swell.index]);

  const energy = useMemo(() => {
    if (isNil(swell?.power)) {
      return `--`;
    }
    return round(swell.power, swell.power < 10 ? 1 : 0);
  }, [swell]);

  return (
    <Grid
      className={styles.swellReadingContainer}
      data-testid={`swell-reading-${swell.index}`}
      item
      key={swell.index}
      onClick={onClickSwellTile}
    >
      <Box
        className={classNames({
          [styles.swellReadingContent]: true,
          [styles.swellReadingContentClickable]: !!setActiveSwell,
          [styles[`swellReadingActive-swell${swell.index}`]]:
            applySwellColors && swell.index === activeSwell,
          [containerClassName]: !!containerClassName,
        })}
        borderColor={activeOrInactiveColor}
      >
        <Grid container spacing={1} justifyContent="space-around">
          <Grid item mobile={5}>
            <Typography variant="callout2" color={activeOrInactiveColor}>
              {round(swell.height, 1)}
              {formatWaveHeight(units.swellHeight, true)}
            </Typography>
          </Grid>
          <Grid item mobile={4}>
            <Typography
              variant="callout2"
              color={activeOrInactiveColor}
              data-testid="swell-reading-period"
            >
              {round(swell.period, 0)}s
            </Typography>
          </Grid>
          <Grid item mobile={3}>
            <Box display="flex" alignItems="center">
              <SwellArrowIcon
                rotation={swell.direction - 180}
                size="medium"
                color={activeOrInactiveColor}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid item mobile={9}>
            <Typography
              variant="caption2"
              color={activeOrInactiveColor}
              className={styles.swellReadingDirection}
            >
              {mapDegreesCardinal(swell.direction)}{' '}
              <Typography
                variant="caption2"
                color={theme.palette.text.secondary}
                className={styles.swellReadingDirection}
              >
                {round(swell.direction)}°
              </Typography>
            </Typography>
          </Grid>
          <Grid item mobile={3}>
            <Typography variant="caption2" color={theme.palette.text.secondary}>
              {swellDirectionSpread}
            </Typography>
          </Grid>
        </Grid>
        <Typography
          variant="callout2"
          color={activeOrInactiveColor}
          data-testid="swell-reading-energy"
        >
          {energy}kJ
        </Typography>
      </Box>
    </Grid>
  );
};

export default SwellReading;
